import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import {
  bumpCashSessionChangeSignal,
  getMergedActiveCashSession,
} from '../../cash/infra-api';
import { showInfo } from '../../shared/ui/appToast';
import { todayIsoDate } from '../infra/expenseListSql';
import {
  flushExpenseSyncSafely,
  getExpenseById,
  invalidateRemoteExpensesReportsCache,
  listSettlementsForExpense,
  requireExpensesModule,
} from '../infra/expensesRepoRead';
import {
  normalizePaidAt,
  type SettleExpenseResult,
} from '../infra/expensesRepoSettlement';
import { cashOutflowCategoryToExpenseSlug, type ExpenseCategorySlug } from '../domain/expenseCategories';
import { expenseVoidRejection, validateExpenseInput } from '../domain/expenseValidation';
import type { ExpenseFiscalInvoiceInput, ExpenseSettlementRow } from '../domain/expenseTypes';
import { validateExpenseFiscalInvoice } from '../domain/expenseFiscalInvoice';
import type { SyncEntity, SyncOperation } from '../../sync/core/syncTypes';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { submitCommand } from '../../sync/core/submitCommand';
import { pullExpensesWhenNeeded } from '../../sync/core/pullExpensesWhenNeeded';
import {
  expenseCommandErrorFromRaw,
  mapExpenseCommandError,
} from '../infra/mapExpenseCommandError';
async function flushExpenseSyncWithFeedback(context: string): Promise<void> {
  const result = await flushExpenseSyncSafely(context);
  if (!result.ok) {
    showInfo(
      'Pendiente de sincronización',
      'Quedó guardado. Se sincroniza cuando haya conexión.'
    );
  }
}

async function submitExpenseCommand(params: {
  entity: SyncEntity;
  operation: SyncOperation;
  payload: Record<string, unknown>;
  failMessage: string;
}): Promise<void> {
  const result = await submitCommand({
    entity: params.entity,
    operation: params.operation,
    payload: params.payload,
  });
  if (!result.applied) {
    const raw = result.ingestError?.trim() || params.failMessage;
    const mapped = mapExpenseCommandError(raw);
    const error = expenseCommandErrorFromRaw(raw);
    const itemId =
      typeof params.payload.expenseId === 'string'
        ? params.payload.expenseId
        : typeof params.payload.settlementId === 'string'
          ? params.payload.settlementId
          : undefined;
    captureOpsFailure({
      module: 'expenses',
      op: expenseOpForEntity(params.entity),
      error,
      expected: mapped.expected === true,
      ingestError: result.ingestError,
      applied: false,
      path: 'web-ram',
      itemId,
      extra: {
        entity: params.entity,
        code: mapped.code,
        expenseId: typeof params.payload.expenseId === 'string' ? params.payload.expenseId : undefined,
        settlementId:
          typeof params.payload.settlementId === 'string' ? params.payload.settlementId : undefined,
      },
    });
    throw error;
  }
}

function expenseOpForEntity(entity: SyncEntity): string {
  if (entity === 'expense_void') return 'expenses.void';
  if (entity === 'expense_settlement') return 'expenses.settle';
  if (entity === 'expense_settlement_void') return 'expenses.settle_void';
  return 'expenses.create';
}

async function refreshExpensesAfterWrite(branchId: string): Promise<void> {
  await pullExpensesWhenNeeded(branchId, { force: true, requireFresh: true }).catch((err) => {
    console.warn('[expenseIntake] pull post-write', err);
  });
}

export async function executeCreateExpenseIntake(params: {
  organizationId: string;
  branchId: string;
  categorySlug: ExpenseCategorySlug;
  amount: number;
  expenseDate: string;
  dueDate?: string | null;
  description?: string | null;
  receiptRef?: string | null;
  supplierId?: string | null;
  createdBy?: string | null;
  purchaseOrderId?: string | null;
  stockReceptionId?: string | null;
  paymentStatus?: 'unpaid' | 'paid';
  /** Comprobante fiscal recibido (Libro IVA compras) — opcional, solo RI. */
  fiscal?: ExpenseFiscalInvoiceInput | null;
}): Promise<string> {
  await requireExpensesModule();
        if (params.paymentStatus === 'paid') {
          throw new Error('Registrá el pago aparte. La factura arranca como pendiente.');
        }
        const validation = validateExpenseInput({
          categorySlug: params.categorySlug,
          amount: params.amount,
          description: params.description,
          expenseDate: params.expenseDate,
          dueDate: params.dueDate,
          paymentStatus: params.paymentStatus,
        });
        if (!validation.ok) throw new Error(validation.message);
        if (params.fiscal) {
          const fiscalValidation = validateExpenseFiscalInvoice(params.fiscal);
          if (fiscalValidation) throw new Error(fiscalValidation);
        }

        const expenseId = crypto.randomUUID();
        const createdAt = new Date().toISOString();
        await submitExpenseCommand({
          entity: 'expense',
          operation: 'insert',
          failMessage: 'No se pudo registrar el gasto en el servidor. Reintentá.',
          payload: {
            expenseId,
            branchId: params.branchId,
            categorySlug: params.categorySlug,
            amount: params.amount,
            expenseDate: params.expenseDate,
            description: (params.description ?? '').trim() || null,
            receiptRef: (params.receiptRef ?? '').trim() || null,
            supplierId: params.supplierId ?? null,
            paymentStatus: 'unpaid',
            dueDate: (params.dueDate ?? '').trim() || null,
            purchaseOrderId: params.purchaseOrderId ?? null,
            stockReceptionId: params.stockReceptionId ?? null,
            createdAt,
            createdBy: params.createdBy ?? null,
            // Bloque fiscal (opcional): el RPC lo persiste atómico con el gasto.
            invoiceDocType: params.fiscal?.docType ?? null,
            invoicePv: params.fiscal?.pointOfSale ?? null,
            invoiceNumber: params.fiscal?.invoiceNumber ?? null,
            supplierTaxId: params.fiscal?.supplierTaxId ?? null,
            supplierName: params.fiscal?.supplierName ?? null,
            invoiceIvaBreakdown: params.fiscal?.ivaBreakdown ?? null,
            invoicePerceptions: params.fiscal?.perceptions ?? null,
          },
        });
        await refreshExpensesAfterWrite(params.branchId);
        void logBusinessAuditEvent('expense.create', {
          branchId: params.branchId,
          expenseId,
          categorySlug: params.categorySlug,
          amount: params.amount,
        });
        invalidateRemoteExpensesReportsCache(params.branchId);
        return expenseId;
}

/** Completa el bloque fiscal de un gasto activo que aún no tiene comprobante (factura tardía). */
export async function executeUpdateExpenseFiscalIntake(params: {
  expenseId: string;
  branchId: string;
  fiscal: ExpenseFiscalInvoiceInput;
  operationId?: string;
}): Promise<void> {
  await requireExpensesModule();
  const fiscalValidation = validateExpenseFiscalInvoice(params.fiscal);
  if (fiscalValidation) throw new Error(fiscalValidation);

  const expense = await getExpenseById(params.expenseId, { branchId: params.branchId });
  if (!expense) throw new Error('No encontré el gasto.');
  if (expense.branchId !== params.branchId) {
    throw new Error('Ese gasto no es de esta sucursal.');
  }
  if (expense.status !== 'active') throw new Error('Ese gasto no está activo.');
  if (expense.invoiceDocType != null) {
    throw new Error('Ese gasto ya tiene comprobante fiscal.');
  }

  const operationId = params.operationId ?? `${params.expenseId}:fiscal`;
  await submitExpenseCommand({
    entity: 'expense',
    operation: 'update',
    failMessage: 'No pude guardar el comprobante fiscal. Reintentá.',
    payload: {
      expenseId: params.expenseId,
      branchId: params.branchId,
      operationId,
      invoiceDocType: params.fiscal.docType,
      invoicePv: params.fiscal.pointOfSale,
      invoiceNumber: params.fiscal.invoiceNumber,
      supplierTaxId: params.fiscal.supplierTaxId,
      supplierName: params.fiscal.supplierName,
      invoiceIvaBreakdown: params.fiscal.ivaBreakdown,
      invoicePerceptions: params.fiscal.perceptions,
    },
  });
  await refreshExpensesAfterWrite(params.branchId);
  void logBusinessAuditEvent('expense.fiscal_update', {
    branchId: params.branchId,
    expenseId: params.expenseId,
    docType: params.fiscal.docType,
  });
  invalidateRemoteExpensesReportsCache(params.branchId);
}

export async function executeVoidExpenseIntake(params: {
  expenseId: string;
  branchId: string;
  voidedBy?: string;
  voidReason?: string;
}): Promise<void> {
  await requireExpensesModule();
        const expense = await getExpenseById(params.expenseId, { branchId: params.branchId });
        if (!expense) throw new Error('No encontré el gasto.');
        if (expense.branchId !== params.branchId) {
          throw new Error('Ese gasto no es de esta sucursal.');
        }
        if (expense.status === 'voided') return;

        const settlements = await listSettlementsForExpense(params.expenseId);
        const activeSettlementCount = settlements.filter((s) => s.status === 'active').length;
        const rejection = expenseVoidRejection({
          activeSettlementCount,
          reason: params.voidReason ?? '',
        });
        if (rejection) throw new Error(rejection);

        const voidedAt = new Date().toISOString();
        const voidReason = (params.voidReason ?? '').trim();
        await submitExpenseCommand({
          entity: 'expense_void',
          operation: 'insert',
          failMessage: 'No se pudo anular el gasto en el servidor. Reintentá.',
          payload: {
            expenseId: params.expenseId,
            voidedAt,
            voidedBy: params.voidedBy ?? null,
            voidReason,
          },
        });
        await refreshExpensesAfterWrite(params.branchId);
        void logBusinessAuditEvent('expense.void', {
          branchId: params.branchId,
          expenseId: params.expenseId,
          voidReason,
        });
        invalidateRemoteExpensesReportsCache(params.branchId);
        return;
}

export async function executeSettleExpenseIntake(params: {
  expenseId: string;
  branchId: string;
  method: ExpenseSettlementRow['method'];
  amount: number;
  categorySlug: ExpenseCategorySlug;
  cashSessionId?: string | null;
  note?: string | null;
  createdBy?: string | null;
  paidAt?: string | null;
}): Promise<SettleExpenseResult> {
  await requireExpensesModule();
        if (!Number.isFinite(params.amount) || params.amount <= 0) {
          throw new Error('El monto debe ser mayor a cero.');
        }
        const expense = await getExpenseById(params.expenseId, { branchId: params.branchId });
        if (!expense || expense.status !== 'active') {
          throw new Error('No se encontró la factura.');
        }
        if (expense.branchId !== params.branchId) {
          throw new Error('Esa factura no es de esta sucursal.');
        }
        if (expense.paymentStatus === 'paid') {
          throw new Error('La factura ya está pagada.');
        }
        const residual = Math.max(0, expense.amount - expense.amountPaid);
        // #752: no permitir overpay — sin epsilon de tolerancia
        if (params.amount > residual) {
          throw new Error('Ese monto es más que el saldo de la factura.');
        }

        let cashSessionId = params.cashSessionId ?? null;
        let cashOutflowId: string | null = null;
        if (params.method === 'cash_drawer') {
          if (!cashSessionId) {
            throw new Error('Abrí la caja para pagar en efectivo.');
          }
          const active = await getMergedActiveCashSession(params.branchId, { force: true });
          if (!active || active.id !== cashSessionId) {
            throw new Error('Abrí la caja para registrar el pago en efectivo.');
          }
          cashOutflowId = crypto.randomUUID();
        }

        const settlementId = crypto.randomUUID();
        const paidAt = normalizePaidAt(params.method, params.paidAt);
        const note = (params.note ?? '').trim() || null;
        await submitExpenseCommand({
          entity: 'expense_settlement',
          operation: 'insert',
          failMessage: 'No se pudo registrar el pago en el servidor. Reintentá.',
          payload: {
            settlementId,
            expenseId: params.expenseId,
            branchId: params.branchId,
            method: params.method,
            amount: params.amount,
            categorySlug: params.categorySlug,
            cashSessionId,
            cashOutflowId,
            note,
            paidAt,
            createdBy: params.createdBy ?? null,
          },
        });
        await refreshExpensesAfterWrite(params.branchId);
        if (cashOutflowId) bumpCashSessionChangeSignal();
        void logBusinessAuditEvent('expense.settlement', {
          branchId: params.branchId,
          expenseId: params.expenseId,
          settlementId,
          method: params.method,
          amount: params.amount,
          paidAt,
          note,
          categorySlug: params.categorySlug,
        });
        invalidateRemoteExpensesReportsCache(params.branchId);
        return {
          settlementId,
          expenseId: params.expenseId,
          branchId: params.branchId,
          method: params.method,
          amount: params.amount,
          categorySlug: params.categorySlug,
          cashSessionId,
          cashOutflowId,
          note,
          paidAt,
          createdBy: params.createdBy ?? null,
        };
}

export async function executeVoidSettlementIntake(params: {
  settlementId: string;
  branchId: string;
  voidedBy?: string | null;
  voidReason?: string;
}): Promise<void> {
  await requireExpensesModule();
        const { getWebExpensesSnapshotCache } = await import('../infra/webExpensesSnapshotCache');
        const cached = getWebExpensesSnapshotCache();
        const settlement = cached?.settlements.find((s) => s.settlementId === params.settlementId);
        if (!settlement) throw new Error('No encontré ese pago.');
        if (settlement.settlementStatus === 'voided') return;

        // Autorización por sucursal: el pago pertenece a la factura padre.
        // Si conocemos su branch tiene que coincidir; si falta, lo resolvemos
        // por la factura — sin metadata no hay allow implícito.
        if (settlement.expenseBranchId && settlement.expenseBranchId !== params.branchId) {
          throw new Error('Ese pago no es de esta sucursal.');
        }
        if (!settlement.expenseBranchId) {
          const expense = await getExpenseById(settlement.expenseId, { branchId: params.branchId });
          if (expense && expense.branchId !== params.branchId) {
            throw new Error('Ese pago no es de esta sucursal.');
          }
        }

        const voidedAt = new Date().toISOString();
        // Misma regla que la UI: motivo obligatorio ≥3 chars (el void queda auditado).
        const voidReason = (params.voidReason ?? '').trim();
        if (voidReason.length < 3) {
          throw new Error('Contanos por qué se anula este pago (mínimo 3 caracteres).');
        }
        await submitExpenseCommand({
          entity: 'expense_settlement_void',
          operation: 'insert',
          failMessage: 'No se pudo anular el pago en el servidor. Reintentá.',
          payload: {
            settlementId: params.settlementId,
            branchId: params.branchId,
            voidedAt,
            voidedBy: params.voidedBy ?? null,
            voidReason,
          },
        });
        await refreshExpensesAfterWrite(params.branchId);
        if (settlement.method === 'cash_drawer') bumpCashSessionChangeSignal();
        void logBusinessAuditEvent('expense.settlement.void', {
          branchId: params.branchId,
          expenseId: settlement.expenseId,
          settlementId: params.settlementId,
          method: settlement.method,
          amount: settlement.settlementAmount,
          voidReason,
          before: { status: 'active' },
          after: { status: 'voided' },
        });
        invalidateRemoteExpensesReportsCache(params.branchId);
        return;
}

export async function executeCreateExpenseWithCashSettlementIntake(params: {
  organizationId: string;
  branchId: string;
  cashSessionId: string;
  category: Parameters<typeof cashOutflowCategoryToExpenseSlug>[0];
  amount: number;
  note?: string;
  createdBy?: string;
}): Promise<{ expenseId: string; settlementId: string; outflowId: string }> {
  // #745: atomic create+settle — un solo comando al backend en vez de
  // create + settle + void-compensate. Si algo falla, el rollback del
  // backend deja todo como si nunca hubiera pasado.
  const categorySlug = cashOutflowCategoryToExpenseSlug(params.category);
  const expenseDate = todayIsoDate();
  const expenseId = crypto.randomUUID();
  const settlementId = crypto.randomUUID();
  const cashOutflowId = crypto.randomUUID();
  const createdAt = new Date().toISOString();

  await requireExpensesModule();
        const validation = validateExpenseInput({
          categorySlug,
          amount: params.amount,
          description: params.note,
          expenseDate,
          paymentStatus: 'paid',
        });
        if (!validation.ok) throw new Error(validation.message);

        // Validar cash session abierta antes de mandar el comando
        const { getMergedActiveCashSession } = await import('../../cash/infra-api');
        const active = await getMergedActiveCashSession(params.branchId, { force: true });
        if (!active || active.id !== params.cashSessionId) {
          throw new Error('Abrí la caja para registrar el pago en efectivo.');
        }

        await submitExpenseCommand({
          entity: 'expense',
          operation: 'insert',
          failMessage: 'No se pudo registrar el gasto pagado en el servidor. Reintentá.',
          payload: {
            expenseId,
            branchId: params.branchId,
            categorySlug,
            amount: params.amount,
            expenseDate,
            description: (params.note ?? '').trim() || null,
            supplierId: null,
            paymentStatus: 'paid',
            dueDate: null,
            purchaseOrderId: null,
            stockReceptionId: null,
            createdAt,
            createdBy: params.createdBy ?? null,
            // #745: settlement fields para que el backend haga create+settle atómico
            settlementId,
            settlementMethod: 'cash_drawer',
            settlementAmount: params.amount,
            settlementCategorySlug: categorySlug,
            cashSessionId: params.cashSessionId,
            cashOutflowId,
            settlementNote: (params.note ?? '').trim() || null,
            paidAt: createdAt,
          },
        });
        await refreshExpensesAfterWrite(params.branchId);
        bumpCashSessionChangeSignal();
        void logBusinessAuditEvent('expense.create_with_settlement', {
          branchId: params.branchId,
          expenseId,
          settlementId,
          method: 'cash_drawer',
          amount: params.amount,
          categorySlug,
        });
        invalidateRemoteExpensesReportsCache(params.branchId);
        return {
          expenseId,
          settlementId,
          outflowId: cashOutflowId,
        };
}
